import { useEffect, useRef } from 'react'

interface NotificationOptions {
  title: string
  body: string
  icon?: string
}

// Singleton AudioContext for iOS compatibility
let audioContext: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    } catch {
      return null
    }
  }
  return audioContext
}

// Call this on user interaction (e.g., starting timer) to unlock audio on iOS
export function unlockAudio() {
  const ctx = getAudioContext()
  if (ctx && ctx.state === 'suspended') {
    ctx.resume()
  }

  // Also play a silent sound to fully unlock on iOS
  if (ctx) {
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()
    gainNode.gain.setValueAtTime(0, ctx.currentTime)
    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)
    oscillator.start()
    oscillator.stop(ctx.currentTime + 0.001)
  }
}

// Generate alarm sound using Web Audio API
function playAlarmSound() {
  const ctx = getAudioContext()
  if (!ctx) return

  // Resume if suspended (fallback)
  if (ctx.state === 'suspended') {
    ctx.resume()
  }

  try {
    // Play a pleasant chime sequence
    const frequencies = [523.25, 659.25, 783.99, 1046.50] // C5, E5, G5, C6

    frequencies.forEach((freq, index) => {
      const oscillator = ctx.createOscillator()
      const gainNode = ctx.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(ctx.destination)

      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(freq, ctx.currentTime)

      const startTime = ctx.currentTime + index * 0.15
      const duration = 0.3

      gainNode.gain.setValueAtTime(0, startTime)
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.05)
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration)

      oscillator.start(startTime)
      oscillator.stop(startTime + duration)
    })

    // Play a second sequence after a short pause
    setTimeout(() => {
      try {
        if (ctx.state === 'closed') return

        frequencies.forEach((freq, index) => {
          const oscillator = ctx.createOscillator()
          const gainNode = ctx.createGain()

          oscillator.connect(gainNode)
          gainNode.connect(ctx.destination)

          oscillator.type = 'sine'
          oscillator.frequency.setValueAtTime(freq, ctx.currentTime)

          const startTime = ctx.currentTime + index * 0.15
          const duration = 0.3

          gainNode.gain.setValueAtTime(0, startTime)
          gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.05)
          gainNode.gain.linearRampToValueAtTime(0, startTime + duration)

          oscillator.start(startTime)
          oscillator.stop(startTime + duration)
        })
      } catch {
        // Ignore errors on second chime
      }
    }, 800)
  } catch (error) {
    console.warn('Could not play alarm sound:', error)
  }
}

// Play explore completion sound (discovery chime)
function playExploreSound() {
  const ctx = getAudioContext()
  if (!ctx) return

  if (ctx.state === 'suspended') {
    ctx.resume()
  }

  try {
    // A magical discovery sound - ascending sparkle
    const frequencies = [392, 523.25, 659.25, 784] // G4, C5, E5, G5

    frequencies.forEach((freq, index) => {
      const oscillator = ctx.createOscillator()
      const gainNode = ctx.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(ctx.destination)

      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(freq, ctx.currentTime)

      const startTime = ctx.currentTime + index * 0.12
      const duration = 0.35

      gainNode.gain.setValueAtTime(0, startTime)
      gainNode.gain.linearRampToValueAtTime(0.25, startTime + 0.04)
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration)

      oscillator.start(startTime)
      oscillator.stop(startTime + duration)
    })
  } catch {
    // Ignore audio errors
  }
}

// Play warning sound for decay
function playWarningSound() {
  const ctx = getAudioContext()
  if (!ctx) return

  if (ctx.state === 'suspended') {
    ctx.resume()
  }

  try {
    const frequencies = [392, 349.23, 329.63] // G4, F4, E4 - descending warning

    frequencies.forEach((freq, index) => {
      const oscillator = ctx.createOscillator()
      const gainNode = ctx.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(ctx.destination)

      oscillator.type = 'triangle'
      oscillator.frequency.setValueAtTime(freq, ctx.currentTime)

      const startTime = ctx.currentTime + index * 0.2
      const duration = 0.25

      gainNode.gain.setValueAtTime(0, startTime)
      gainNode.gain.linearRampToValueAtTime(0.25, startTime + 0.03)
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration)

      oscillator.start(startTime)
      oscillator.stop(startTime + duration)
    })
  } catch {
    // Ignore audio errors
  }
}

// Request notification permission
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    return false
  }

  if (Notification.permission === 'granted') {
    return true
  }

  if (Notification.permission === 'denied') {
    return false
  }

  const permission = await Notification.requestPermission()
  return permission === 'granted'
}

// Get current notification permission status
export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!('Notification' in window)) {
    return 'unsupported'
  }
  return Notification.permission
}

// Show a browser notification
function showNotification({ title, body, icon, tag = 'timer-complete' }: NotificationOptions & { tag?: string }) {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return
  }

  try {
    const notification = new Notification(title, {
      body,
      icon: icon || '/favicon.svg',
      tag,
      requireInteraction: true,
    })

    notification.onclick = () => {
      window.focus()
      notification.close()
    }

    // Auto-close after 10 seconds
    setTimeout(() => notification.close(), 10000)
  } catch (error) {
    console.warn('Could not show notification:', error)
  }
}

// Hook to trigger notification when timer completes
export function useTimerNotification(isComplete: boolean, energy: number) {
  const hasNotified = useRef(false)
  const wasComplete = useRef(false)

  useEffect(() => {
    // Detect transition from not complete to complete
    if (isComplete && !wasComplete.current && !hasNotified.current) {
      hasNotified.current = true

      // Play alarm sound
      playAlarmSound()

      // Show browser notification
      showNotification({
        title: 'Focus Complete!',
        body: `Great work! You earned ${energy} energy. Collect it now!`,
      })
    }

    wasComplete.current = isComplete

    // Reset when timer becomes inactive
    if (!isComplete) {
      hasNotified.current = false
    }
  }, [isComplete, energy])
}

// Hook to notify when decay starts
export function useDecayNotification(isDecaying: boolean, currentEnergy: number) {
  const hasNotified = useRef(false)
  const wasDecaying = useRef(false)

  useEffect(() => {
    // Detect transition to decaying state
    if (isDecaying && !wasDecaying.current && !hasNotified.current) {
      hasNotified.current = true

      // Play warning sound
      playWarningSound()

      // Show warning notification
      showNotification({
        title: 'Energy Decaying!',
        body: `Your energy is now decaying! Current: ${currentEnergy}. Collect it quickly!`,
      })
    }

    wasDecaying.current = isDecaying

    // Reset when no longer decaying
    if (!isDecaying) {
      hasNotified.current = false
    }
  }, [isDecaying, currentEnergy])
}

interface ExploreResult {
  type: 'travel' | 'hunt'
  place?: string
  huntResult?: {
    success: boolean
    echo?: { name: string }
    isGlimmering?: boolean
  }
}

// Hook to notify when explore activity (hunt/travel) completes
export function useExploreNotification(
  pending: ExploreResult | null,
  getPlaceName?: (placeId: string) => string
) {
  const lastPendingRef = useRef<ExploreResult | null>(null)

  useEffect(() => {
    // Detect new pending result (transition from null or different result)
    if (pending && pending !== lastPendingRef.current) {
      // Play explore completion sound
      playExploreSound()

      // Show appropriate notification
      if (pending.type === 'travel' && pending.place) {
        const placeName = getPlaceName?.(pending.place) || pending.place
        showNotification({
          title: 'Travel Complete!',
          body: `You have arrived at ${placeName}.`,
          tag: 'explore-complete',
        })
      } else if (pending.type === 'hunt' && pending.huntResult) {
        if (pending.huntResult.success && pending.huntResult.echo) {
          const glimmerText = pending.huntResult.isGlimmering ? ' (Glimmering!)' : ''
          showNotification({
            title: 'Hunt Complete!',
            body: `You found: ${pending.huntResult.echo.name}${glimmerText}`,
            tag: 'explore-complete',
          })
        } else {
          showNotification({
            title: 'Hunt Complete',
            body: 'The hunt is over. Check your results!',
            tag: 'explore-complete',
          })
        }
      }
    }

    lastPendingRef.current = pending
  }, [pending, getPlaceName])
}
